/**
 * Push channel — per-page components for the domain-app shell.
 * Each page wires the usePushCommunications hook to the relevant prop-driven tab.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconLayoutDashboard,
  IconSend,
  IconTemplate,
  IconPencil,
  IconUsersGroup,
  IconSettings,
  IconSpeakerphone,
} from '@tabler/icons-react';
import { Group, Loader } from '../../../../components/ui';
import { Card } from '../../../../components/ui/data-display';
import { ChannelPage } from '../_shared/ChannelPage';
import { ChannelDashboard } from '../_shared/ChannelDashboard';
import { ProviderSettingsForm } from '../_shared/ProviderSettingsForm';
import { BulkSendWizard } from '../_shared/BulkSendWizard';
import { CampaignsList, CampaignDetail } from '../_shared/CampaignsView';
import { usePushCommunications } from './hooks/usePushCommunications';
import {
  PushQueueTab,
  PushTemplatesTab,
  PushTestTab,
  PushQueueModal,
  PushTemplateModal,
} from './components';
import type { AppPushMessageModelView, AppPushTemplateModelView, PushTestData } from './types';
import { encodePushBody } from './types';

export function PushDashboardPage() {
  return (
    <ChannelPage
      icon={<IconLayoutDashboard size={28} />}
      title="Push Notifications"
      subtitle="Overview of the push channel. Use the left menu to compose, review the outbox, manage templates, and configure providers."
    >
      <ChannelDashboard channel="push" />
    </ChannelPage>
  );
}

export function PushComposePage() {
  const { loading, templates, fetchTemplates, createPush } = usePushCommunications();

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSend = async (data: PushTestData) => {
    // The recipient picker resolves to one or more devices; fall back to a single manual token.
    const recipients = data.recipients && data.recipients.length > 0
      ? data.recipients
      : [{ deviceToken: data.deviceToken || '', deviceID: data.deviceID || '', appId: undefined, platform: undefined }];

    const payload = JSON.stringify({
      sound: data.sound || 'default',
      badge: data.badge,
      data: data.data || {},
      userId: data.userId,
    });

    // Queue one push per device token.
    for (const r of recipients) {
      await createPush({
        token: r.deviceToken,
        deviceID: r.deviceID,
        appId: r.appId,
        platform: r.platform,
        title: data.title,
        body: data.body,
        imageUrl: data.imageUrl || '',
        notificationDate: new Date().toISOString(),
        priority: data.priority,
        scheduledDateTime: data.scheduledDateTime || null,
        templateID: data.templateId || null,
        payload,
        status: 'Pending',
        isSent: false,
        hasFailed: false,
        retryCount: 0,
        maxRetryCount: 3,
      } as any);
    }
  };

  return (
    <ChannelPage icon={<IconPencil size={28} />} title="Compose" subtitle="Compose and queue a push notification.">
      <Card withBorder padding="lg">
        <PushTestTab templates={templates} loading={loading} onSend={handleSend} />
      </Card>
    </ChannelPage>
  );
}

export function PushBulkSendPage() {
  return (
    <ChannelPage icon={<IconUsersGroup size={28} />} title="Bulk Send" subtitle="Send to many recipients via a campaign.">
      <BulkSendWizard channel="push" />
    </ChannelPage>
  );
}

export function PushCampaignsPage() {
  const navigate = useNavigate();
  return (
    <ChannelPage icon={<IconSpeakerphone size={28} />} title="Campaigns" subtitle="Bulk campaign history with delivery counts and per-recipient status.">
      <CampaignsList channel="push" onOpen={(id) => navigate(`/admin/communications/push/campaigns/${id}`)} />
    </ChannelPage>
  );
}

export function PushCampaignDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  return (
    <ChannelPage
      icon={<IconSpeakerphone size={28} />}
      title="Campaigns"
      subtitle="Delivery counts and per-recipient status."
      onBack={() => navigate('/admin/communications/push/campaigns')}
    >
      <CampaignDetail campaignId={Number(id)} />
    </ChannelPage>
  );
}

export function PushOutboxPage() {
  const {
    loading,
    queueItems,
    fetchQueue,
    deleteQueueItem,
    bulkDeleteQueueItems,
    retryFailedItem,
    updatePriority,
    updateQueueItem,
  } = usePushCommunications();

  const [modalOpened, setModalOpened] = useState(false);
  const [selected, setSelected] = useState<AppPushMessageModelView | null>(null);
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  return (
    <ChannelPage icon={<IconSend size={28} />} title="Outbox" subtitle="Queued & sent push notifications with delivery status.">
      <Card withBorder padding="lg">
        {loading && queueItems.length === 0 ? (
          <Group justify="center" p="xl">
            <Loader size="lg" />
          </Group>
        ) : (
          <PushQueueTab
            queueItems={queueItems}
            loading={loading}
            onRefresh={fetchQueue}
            onView={(item: AppPushMessageModelView) => { setSelected(item); setMode('view'); setModalOpened(true); }}
            onEdit={(item: AppPushMessageModelView) => { setSelected(item); setMode('edit'); setModalOpened(true); }}
            onDelete={deleteQueueItem}
            onBulkDelete={bulkDeleteQueueItems}
            onRetry={retryFailedItem}
            onUpdatePriority={updatePriority}
          />
        )}
      </Card>
      <PushQueueModal
        opened={modalOpened}
        onClose={() => { setModalOpened(false); setSelected(null); }}
        item={selected}
        mode={mode}
        onSave={async (data: Partial<AppPushMessageModelView>) => {
          if (selected) {
            await updateQueueItem(selected.messageID, data as any);
            setModalOpened(false);
            setSelected(null);
          }
        }}
        loading={loading}
      />
    </ChannelPage>
  );
}

export function PushTemplatesPage() {
  const {
    loading,
    templates,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = usePushCommunications();

  const [modalOpened, setModalOpened] = useState(false);
  const [selected, setSelected] = useState<AppPushTemplateModelView | null>(null);
  const [mode, setMode] = useState<'view' | 'edit' | 'create'>('view');

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return (
    <ChannelPage icon={<IconTemplate size={28} />} title="Templates" subtitle="Reusable push templates.">
      <Card withBorder padding="lg">
        {loading && templates.length === 0 ? (
          <Group justify="center" p="xl">
            <Loader size="lg" />
          </Group>
        ) : (
          <PushTemplatesTab
            templates={templates}
            loading={loading}
            onRefresh={fetchTemplates}
            onView={(t: AppPushTemplateModelView) => { setSelected(t); setMode('view'); setModalOpened(true); }}
            onEdit={(t: AppPushTemplateModelView) => { setSelected(t); setMode('edit'); setModalOpened(true); }}
            onDelete={deleteTemplate}
            onCreate={() => { setSelected(null); setMode('create'); setModalOpened(true); }}
          />
        )}
      </Card>
      <PushTemplateModal
        opened={modalOpened}
        onClose={() => { setModalOpened(false); setSelected(null); }}
        template={selected}
        mode={mode}
        onSave={async (data: any) => {
          // POST/PUT api/template/push bind the AppPushTemplate ENTITY, whose fields are
          // Name / Template / TemplateID / AppID — not the form's templateName / body. Sending the
          // form shape straight through wrote a blank name and body on create, and failed the
          // "Template ID mismatch" check on every update. Map it explicitly, and carry the
          // untouched fields (appID, description) through so a save doesn't erase them.
          // TemplateID and AppID are ASSIGNED, not generated — the operator types them, so they come
          // from the form. Sending 0 (as this did) makes the insert collide on the primary key.
          const payload = {
            templateID: mode === 'create' ? Number(data.templateID) : selected!.templateID,
            appID: Number(data.appID),
            name: data.templateName,
            template: encodePushBody(data.body),          // stored base64-encoded
            description: selected?.description ?? '',
            mobileAppId: data.mobileAppId ?? null,        // the MOBILE app (selects the APNs topic)
          };

          if (mode === 'create') {
            await createTemplate(payload);
          } else if (selected) {
            await updateTemplate(selected.templateID, payload);
          }
          setModalOpened(false);
          setSelected(null);
        }}
        loading={loading}
      />
    </ChannelPage>
  );
}

export function PushSettingsPage() {
  return (
    <ChannelPage icon={<IconSettings size={28} />} title="Settings" subtitle="Provider credentials (Firebase / APNs) and channel configuration.">
      <ProviderSettingsForm channel="push" />
    </ChannelPage>
  );
}
